import { useEffect, useRef } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { useParams } from 'react-router-dom';

import { PlanStep } from './plan-step';
import { useGetThreadExistsInNewKim } from '@/api/queries/useGetThreadExistsInNewKim';
import NewKimScreenshot from '@/assets/img/new-kim.png';
import { Icon } from '@/new/components/icon';
import { NewChip } from '@/new/components/new-chip';
import { Button } from '@/new/components/shadcn/button';
import { ParameterValue, PlanStage } from '@/types';

interface PlanDrawerProps {
  loadingPlan: boolean;
  executePlan: (plan: PlanStage[]) => void;
  updatePlan: (plan: PlanStage[], step: number, parameterName: string, newValue: ParameterValue) => void;
  planStage: Record<string, string>;
  plan: PlanStage[];
  executing: boolean;
  editable: boolean;
  cancelPlan: () => void;
}

const PlanDrawer = ({
  loadingPlan,
  updatePlan,
  executePlan,
  planStage,
  plan,
  executing,
  editable,
  cancelPlan,
}: PlanDrawerProps) => {
  const container = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Find the index of the step that's currently in-progress
    const inProgressIndex = plan.findIndex((step) => planStage[step.name] === 'in-progress');
    if (inProgressIndex !== -1) {
      const stepElements = containerRef.current?.getElementsByClassName('plan-step');
      if (stepElements?.[inProgressIndex]) {
        const container = containerRef.current;
        const element = stepElements[inProgressIndex];

        // Calculate the scroll position to center the element
        const elementTop = (element as HTMLElement).offsetTop;
        const elementHeight = element.clientHeight;
        const containerHeight = container.clientHeight;
        const scrollTo = elementTop - containerHeight / 2 + elementHeight / 2;

        container.scrollTo({
          top: scrollTo,
          behavior: 'smooth',
        });
      }
    }
  }, [planStage, plan]);

  return (
    <AnimatePresence>
      <motion.div
        className="h-full w-2/5 max-w-[400px] border-r"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex h-full flex-col">
          <div className="flex h-10 w-full items-center gap-2 border-b px-4 text-lg font-medium">
            <Icon className="lucide--list-check h-4 w-4" />
            Plan
          </div>

          <div className="flex flex-1 flex-col justify-between">
            {loadingPlan ? (
              <div className="mx-4 mt-10 flex h-[400px] flex-col items-center justify-center gap-4">
                <p className="text-sm text-gray-500">Building plan...</p>
                <Icon className="lucide--loader-circle h-3 w-3 animate-spin duration-700" />
              </div>
            ) : (
              <div className="flex h-full flex-col justify-between">
                <div>
                  <p className="px-4 pt-3 pb-1 text-sm text-gray-800">Kim will execute the following plan:</p>
                  <div
                    ref={containerRef}
                    className={`mx-4 my-1 max-h-96 overflow-y-auto rounded-lg border bg-gray-50/50 p-4 text-sm text-gray-700 ${
                      executing ? 'pointer-events-auto select-none [&_*]:pointer-events-none' : ''
                    }`}
                  >
                    <motion.div
                      className="relative flex flex-col"
                      variants={container}
                      initial="hidden"
                      animate="visible"
                    >
                      {/* Continuous vertical line */}
                      <div className="absolute top-4 bottom-2 left-[0.7rem] z-0 w-0 border-l-2 border-gray-300"></div>
                      {plan.map((step, index) => (
                        <PlanStep
                          key={index}
                          step={step}
                          index={index}
                          executing={executing}
                          planStage={planStage}
                          updatePlan={(parameterName: string, newValue: ParameterValue) =>
                            updatePlan(plan, index, parameterName, newValue)
                          }
                        />
                      ))}
                    </motion.div>
                  </div>
                </div>
                {editable && (
                  <div className="h-28 px-4 pt-1 pb-4">
                    <div className="py-4 text-xs text-gray-500">
                      You can alter any highlighted{' '}
                      <span className="rounded bg-blue-100 px-1 text-gray-600">options</span> of a plan.
                    </div>
                    <div className="flex flex-row justify-between gap-2">
                      <Button onClick={() => cancelPlan()} disabled={executing || loadingPlan} variant="destructive">
                        Cancel
                        <Icon className="lucide--x h-4 w-4" />
                      </Button>
                      <Button variant={'primary'} onClick={() => executePlan(plan)} disabled={executing || loadingPlan}>
                        Execute Plan
                        <Icon className="lucide--wand-sparkles h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

interface PlanPageProps {
  questionValue: string;
  loading: boolean;
  messages: string[];
}

const PlanPage = ({ questionValue, loading, messages }: PlanPageProps) => {
  const { matterId, subsetId, threadId } = useParams();
  const { refetch: refetchExistsInNewKim } = useGetThreadExistsInNewKim(matterId!, subsetId!, threadId!);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const handleNewKimClick = async () => {
    const { data: existsInNewKim } = await refetchExistsInNewKim();
    const href = existsInNewKim
      ? `/matters/${matterId}/subsets/${subsetId}/new-kim/${threadId}`
      : `/matters/${matterId}/subsets/${subsetId}/new-kim?question=${encodeURIComponent(questionValue)}`;
    window.open(href, '_blank');
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const processMessages = (messages: string[]): React.ReactNode => {
    if (!messages || messages.length === 0) return '';

    return messages.map((message, messageIndex) => {
      const paragraphs = message.split('<br>');

      return (
        <motion.div
          key={messageIndex}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: messageIndex * 0.1 }}
          className="flex items-center gap-3 rounded-md p-1"
        >
          <Icon className="lucide--circle h-2 w-2 text-teal-500" />

          <div className="min-w-0 flex-1 space-y-1 border-b border-dashed">
            {paragraphs.map((paragraph, index) => {
              // Trim leading/trailing commas and spaces
              const trimmedParagraph = paragraph.trim().replace(/^,+|,+$/g, '');
              if (!trimmedParagraph) return null;

              // Replace numbers with bold numbers using JSX
              const parts = trimmedParagraph.split(/(\d+)/);
              return (
                <p key={index} className="text-xs leading-relaxed text-gray-700">
                  {parts.map((part, partIndex) =>
                    isNaN(Number(part)) ? (
                      part
                    ) : (
                      <strong key={partIndex} className="font-semibold text-gray-900">
                        {part}
                      </strong>
                    ),
                  )}
                </p>
              );
            })}
          </div>
        </motion.div>
      );
    });
  };

  return (
    <div className="mx-10 min-w-0 flex-1">
      {/* Question Header */}
      <div className="mt-6 mb-12 text-gray-700">
        <h1 className="mb-4 flex items-center gap-2 text-lg font-medium text-gray-700">
          <Icon className="lucide--message-circle-question text-lg text-teal-500/60" />
          Question
        </h1>
        <div className="max-h-[150px] overflow-y-auto rounded-md border-l-2 border-teal-500/30 bg-gray-50/50 px-4 py-3 text-sm text-gray-600">
          <div className="prose prose-sm max-w-none">
            <p className="leading-relaxed text-gray-700">{questionValue}</p>
          </div>
        </div>

        <div className="mt-3 flex w-fit gap-4.5 p-1">
          <div className="relative aspect-video w-54 overflow-hidden rounded-lg border bg-gray-50/60">
            <NewChip className="top-auto right-auto bottom-0 left-0 m-1.5" />

            <div className="relative h-full w-full overflow-clip">
              <img
                src={NewKimScreenshot}
                alt="Improved kim experience."
                className="absolute top-4 left-4 origin-top-left scale-160 rounded-sm border"
              />
            </div>
          </div>

          <div className="space-y-2.25 py-2">
            <div className="max-w-xs">
              <span className="font-serif text-xl leading-5 font-medium tracking-tight text-gray-700">
                Deeper research and <br /> multi-turn conversations
              </span>
              <Icon className="lucide--sparkle ml-1.75 inline-block text-xs text-teal-400" />
            </div>

            <Button size="sm" onClick={handleNewKimClick} className="-ml-0.5 font-normal">
              View this question in the improved Kim experience <Icon className="lucide--arrow-up-right" />
            </Button>

            <p className="ml-0.5 text-xs text-gray-400">Opens in a new tab</p>
          </div>
        </div>
      </div>

      {loading && (
        <div className="space-y-6">
          {/* Execution Status */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 font-medium text-gray-800">
              <Icon className="lucide--rocket h-4 w-4 text-teal-500" />
              Executing Plan
            </div>
            <AnimatePresence>
              <motion.div className="max-h-96 overflow-y-auto rounded-md border border-gray-100 bg-gray-50/50 p-4">
                <div className="space-y-3">
                  {processMessages(messages)}
                  <div ref={messagesEndRef} />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
};

export { PlanDrawer, PlanPage };
